<template>
  <b-modal
    id="modal-editar-distrito"
    ref="modalEditarDistrito"
    centered
    hide-footer
    @hidden="onHidden"
  >
    <template #modal-title>
      <div class="modal-header-custom">
        <h5 class="mb-0">Editar distrito sanitário</h5>
        <small class="modal-subtitle">
          Selecione um distrito para visualizar e editar seus dados
        </small>
      </div>
    </template>

    <ValidationObserver ref="form">
      <b-alert
        v-if="modalAlert.show"
        :variant="modalAlert.variant"
        show
        class="mb-3"
      >
        {{ modalAlert.message }}
      </b-alert>

      <div v-if="modalLoading" class="text-center">
        <b-spinner label="Carregando" />
      </div>

      <b-form v-else @submit.prevent="onSubmit">
        <validation-provider v-slot="{ errors }" rules="required">
          <div class="form-group">
            <label>Distrito sanitário</label>

            <vue-select
              :options="districtOptionsFiltered"
              label="no_distrito"
              v-model="distritoSelecionado"
              :reduce="(option) => option.id"
              :clearable="false"
            >
              <template slot="option" slot-scope="option">
                {{ option.no_distrito }}
              </template>
              <div slot="no-options">Não há opções correspondentes</div>
            </vue-select>

            <div class="error">{{ errors[0] }}</div>
          </div>
        </validation-provider>

        <div v-if="distritoSelecionado" class="row">
          <div class="col-md-6">
            <validation-provider v-slot="{ errors }" rules="required">
              <div class="form-group">
                <label for="distrito-nome">Nome</label>
                <input
                  id="distrito-nome"
                  type="text"
                  class="form-control"
                  v-model.trim="formLocal.no_distrito"
                />
                <div class="error">{{ errors[0] }}</div>
              </div>
            </validation-provider>
          </div>

          <div class="col-md-6">
            <validation-provider v-slot="{ errors }">
              <div class="form-group">
                <label for="distrito-gerente">Gerente</label>
                <input
                  id="distrito-gerente"
                  type="text"
                  class="form-control"
                  v-model.trim="formLocal.no_gerente"
                />
                <div class="error">{{ errors[0] }}</div>
              </div>
            </validation-provider>
          </div>

          <div class="col-md-6">
            <validation-provider v-slot="{ errors }" rules="cpf">
              <div class="form-group">
                <label for="distrito-cpf-gerente">CPF Gerente</label>
                <input
                  id="distrito-cpf-gerente"
                  type="text"
                  class="form-control"
                  v-model.trim="formLocal.cpf_gerente"
                  v-inputmask="maskCpf"
                />
                <div class="error">{{ errors[0] }}</div>
              </div>
            </validation-provider>
          </div>

          <div class="col-md-6">
            <validation-provider v-slot="{ errors }" rules="required">
              <div class="form-group">
                <label for="distrito-ativo">Ativo</label>
                <select
                  id="distrito-ativo"
                  class="form-control"
                  v-model="formLocal.ativo"
                >
                  <option :value="1">Sim</option>
                  <option :value="0">Não</option>
                </select>
                <div class="error">{{ errors[0] }}</div>
              </div>
            </validation-provider>
          </div>

          <div class="col-md-12">
            <div class="form-group">
              <label for="distrito-anotacoes">Observações</label>
              <textarea
                id="distrito-anotacoes"
                class="form-control"
                rows="4"
                v-model.trim="formLocal.anotacoes"
                placeholder="Notas ou observações sobre o distrito sanitário."
              ></textarea>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <b-button
            variant="primary"
            type="submit"
            :disabled="saving || !distritoSelecionado"
          >
            <span v-if="loadingAction === 'salvar'">
              Salvando...
              <b-spinner small class="ml-2" />
            </span>
            <span v-else>Salvar</span>
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import vueSelect from 'vue-select'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { validations } from '@/mixins/validations'
import Masks from '@/mixins/masks'

validations()

export default {
  name: 'ModalDistrito',
  components: {
    vueSelect,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    modalLoading: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      default: () => ({
        id: null,
        no_distrito: null,
        cpf_gerente: null,
        no_gerente: null,
        ativo: 1,
        anotacoes: null,
      }),
    },
    districtOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      saving: false,
      loadingAction: null,
      distritoSelecionado: null,
      modalAlert: {
        show: false,
        variant: 'success',
        message: '',
      },
      formLocal: {
        id: null,
        no_distrito: null,
        cpf_gerente: null,
        no_gerente: null,
        ativo: 1,
        anotacoes: null,
      },
    }
  },
  computed: {
    maskCpf() {
      return Masks['cpf']
    },

    /**
     * Retorna apenas os distritos válidos para edição
     *
     * @return {Array}
     */
    districtOptionsFiltered() {
      return this.districtOptions.filter((item) => {
        return !['nao-se-aplica', 'inserir-distrito'].includes(String(item.id))
      })
    },
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.distritoSelecionado = value?.id ?? null
        this.formLocal = {
          id: value?.id ?? null,
          no_distrito: value?.no_distrito ?? null,
          cpf_gerente: value?.cpf_gerente ?? null,
          no_gerente: value?.no_gerente ?? null,
          ativo: value?.ativo ?? 1,
          anotacoes: value?.anotacoes ?? null,
        }
      },
    },

    /**
     * Preenche o formulário ao selecionar um distrito
     *
     * @param {string|number|null} value
     * @return {void}
     */
    distritoSelecionado(value) {
      const distrito = this.districtOptionsFiltered.find((item) => {
        return String(item.id) === String(value)
      })

      if (!distrito) {
        this.formLocal = {
          id: null,
          no_distrito: null,
          cpf_gerente: null,
          no_gerente: null,
          ativo: 1,
          anotacoes: null,
        }
        return
      }

      this.formLocal = {
        id: distrito.id,
        no_distrito: distrito.no_distrito ?? null,
        cpf_gerente: distrito.cpf_gerente ?? null,
        no_gerente: distrito.no_gerente ?? null,
        ativo: distrito.ativo ?? 1,
        anotacoes: distrito.anotacoes ?? null,
      }
    },
  },
  methods: {
    /**
     * Abre o modal de distrito
     */
    abrir() {
      this.$refs.modalEditarDistrito.show()
    },

    /**
     * Fecha o modal de distrito
     */
    fechar() {
      this.$refs.modalEditarDistrito.hide()
    },

    /**
     * Evento executado ao fechar o modal
     */
    onHidden() {
      this.saving = false
      this.loadingAction = null
      this.distritoSelecionado = null
      this.modalAlert = {
        show: false,
        variant: 'success',
        message: '',
      }

      this.$emit('hidden')
    },

    /**
     * Exibe mensagem de sucesso
     * @param {string} message
     */
    exibirSucesso(message) {
      this.modalAlert = {
        show: true,
        variant: 'success',
        message,
      }
    },

    /**
     * Exibe mensagem de erro
     * @param {string} message
     */
    exibirErro(message) {
      this.modalAlert = {
        show: true,
        variant: 'danger',
        message,
      }
    },

    /**
     * Submete o formulário de distrito
     */
    async onSubmit() {
      const isValid = await this.$refs.form.validate()

      if (!isValid || !this.formLocal.id) {
        return
      }

      this.saving = true
      this.loadingAction = 'salvar'
      this.modalAlert.show = false

      this.$emit('salvar', {
        form: { ...this.formLocal },
        onSuccess: this.exibirSucesso,
        onError: this.exibirErro,
        onFinally: () => {
          this.saving = false
          this.loadingAction = null
        },
      })
    },
  },
}
</script>

<style lang="scss">
#modal-editar-distrito {
  .modal-header-custom {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  .modal-subtitle {
    font-size: 12px;
    color: #6c757d;
    font-weight: 400;
    margin-top: 2px;
  }
}
</style>
